<template>
  <div
    class="guest-row flex items-center justify-between px-30 overflow-hidden select-none cursor-pointer mobile:px-20"
    :class="item.snapshotChange"
    :style="{ height: itemHeight + 'px' }"
    @mouseenter="idState.showGuestActions = true"
    @mouseover="idState.showGuestActions = true"
    @mouseleave="idState.showGuestActions = false"
    @touchend.prevent="onTouchEnd"
    @touchmove="touchDragging = true"
    @click="item.snapshotChange !== 'removed' ? $emit('guest-update', item.id) : null"
  >
    <div class="guest-data flex-1 flex flex-row items-center">
<!--      <UIAvatar-->
<!--        :name-length="guest.firstName.length + guest.lastName.length"-->
<!--      />-->
      <AvatarChip
        :title="item.fullName"
        :initials="item.fullName.charAt(0)"
        :name-length="item.fullName.length - 1"
        :size="avatarChipSize"
        :src="item.photoURL"
        color="#f3f3f3"
        :style="{  flex: `0 0 ${this.avatarChipSize}px` }"
        class="mr-25 mobile:mr-15"
      />
      <TransitionGroup
        name="name-vip-animation"
        tag="div"
        class="name-vip-container flex flex-col mr-20 overflow-hidden"
      >
        <div
          key="full-name"
          :title="item.fullName"
          data-hj-suppress
          data-private
          class="full-name font-medium text-primary leading-tight truncate name-vip-animation-item
          mobile:text-base"
        >
          {{ item.fullName }}
        </div>
        <div
          v-if="item.vip"
          key="vip"
          class="name-vip-animation-item self-start font-bold text-xs text-secondary tracking-widest leading-tight py-1
          px-7 mt-1 border border-secondary rounded"
        >
          VIP
        </div>
        <!-- The guest store maps the Firestore `notes` field to `note` -->
        <div
          v-if="item.note"
          key="notes"
          :title="item.note"
          data-hj-suppress
          data-private
          class="notes name-vip-animation-item text-xs text-primary-light leading-tight truncate mt-1"
        >
          {{ item.note }}
        </div>
      </TransitionGroup>
      <div class="status-container small:hidden">
        <p
          :title="guestStatus.name"
          :class="guestStatus.tagClasses"
          class="font-medium text-xs tracking-wider uppercase py-5 rounded"
        >
          {{ guestStatus.name }}
        </p>
      </div>
      <p class="extra-guests-container text-sm text-center mr-20">
        <button
          v-if="item.totalExtraGuests && item.snapshotChange !== 'removed' && !hideCheckIn"
          type="button"
          title="Press to check in or uncheck the extra guests"
          class="extra-guests-button"
          @touchend.stop
          @click.stop="$emit('guest-extras', item.id)"
        >
          {{ extraGuestsFormatter(item.checkedInExtraGuests, item.totalExtraGuests) }}
        </button>
        <span v-else>{{ extraGuestsFormatter(item.checkedInExtraGuests, item.totalExtraGuests) }}</span>
      </p>
      <div class="email-container text-sm overflow-hidden">
        <span
          v-show="item.email"
          :title="item.email"
          data-private
          class="email truncate"
        >
          {{ item.email }}
        </span>
      </div>
    </div>
    <!-- Touch widths (<1024px) hide the hover actions, so check-in gets its own always-visible button -->
    <ElButton
      v-if="showCheckIn && item.snapshotChange !== 'removed'"
      :title="item.status === 2 ? 'Press to reset the check-in of this guest' : 'Press to check in this guest'"
      circle
      class="check-in-guest-button touch-check-in hidden touch:inline-block ml-10"
      :class="{ 'is-checked-in': item.status === 2 }"
      @touchend.native.stop
      @click.native.stop="onCheckInPress($event)"
    >
      <FontAwesomeIcon
        :icon="['far', 'check']"
        class="check-in-guest-icon"
      />
    </ElButton>
    <div
      v-if="!hideEdit || !hideDelete || showCheckIn"
      v-show="idState.showGuestActions && item.snapshotChange !== 'removed'"
      class="guest-actions touch:hidden"
    >
      <ElButton
        v-if="showCheckIn"
        :title="item.status === 2 ? 'Press to reset the check-in of this guest' : 'Press to check in this guest'"
        circle
        class="check-in-guest-button mr-10"
        :class="{ 'is-checked-in': item.status === 2 }"
        @click.native.stop="onCheckInPress($event)"
      >
        <FontAwesomeIcon
          :icon="['far', 'check']"
          class="check-in-guest-icon"
        />
      </ElButton>
      <ElButton
        v-if="!hideEdit"
        title="Press to edit this guest"
        circle
        class="edit-guest-button"
      >
        <FontAwesomeIcon
          :icon="['fas', 'pen']"
          class="edit-guest-icon"
        />
      </ElButton>
      <FontAwesomeIcon
        v-if="!hideDelete"
        :icon="['fas', 'trash']"
        title="Press to delete this guest"
        class="delete-guest-icon align-middle text-danger ml-15 cursor-pointer"
        @click.stop="item.snapshotChange === 'removed' ? null : $emit('guest-delete', item.id)"
      />
    </div>
  </div>
</template>

<script>
import { IdState } from 'vue-virtual-scroller';
import AvatarChip from '@/components/AvatarChip';
import { guestStatus } from '@/utils/utilityFunctions';

export default {
  props: {
    avatarChipSize: Number,
    item: Object,
    itemIndex: Number,
    itemHeight: Number,
    hideDelete: {
      type: Boolean,
      default: false,
    },
    hideEdit: {
      type: Boolean,
      default: false,
    },
    hideCheckIn: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [
    IdState({
      idProp: (vm) => vm.item.id,
    }),
  ],
  components: {
    AvatarChip,
  },
  idState: () => ({ // This replaces data () { ... } so that each item doesn't share it's state
    showGuestActions: false,
    touchDragging: false,
  }),
  computed: {
    // No check-in shortcut for guests without a status (0)
    showCheckIn() {
      return !this.hideCheckIn && Boolean(this.item?.status);
    },
    guestStatus() {
      return guestStatus[this.item?.status] ?? guestStatus.default;
    },
  },
  methods: {
    extraGuestsFormatter(arrivedExtras, invitedExtras) {
      if (!arrivedExtras && !invitedExtras) return '-';

      return `${arrivedExtras || 0}/${invitedExtras} extra guests`;
    },
    onCheckInPress(event) {
      if (event && event.currentTarget) event.currentTarget.blur();
      if (this.item.snapshotChange === 'removed') return;

      this.$emit(this.item.status === 2 ? 'guest-reset-check-in' : 'guest-check-in', this.item.id);
    },
    onTouchEnd() {
      if (this.item.snapshotChange !== 'removed' && !this.touchDragging) {
        this.$emit('guest-update', this.item.id);
      }
      this.touchDragging = false;
    },
  },
};
</script>

<style scoped>
.guest-row {
  transition: box-shadow 120ms easeInOutQuart, background-color 300ms easeInOutQuart;

  &.added,
  &.modified,
  &.removed {
    .full-name,
    .status-container .el-tag,
    .extra-guests-container,
    .email-container {
      @apply font-bold;
    }
  }

  &.added {
    @apply bg-guest-change-added;
  }

  &.modified {
    @apply bg-guest-change-modified;
  }

  &.removed {
    @apply bg-guest-change-removed;
  }

  .guest-data {
    .name-vip-container {
      flex: 0 0 240px;
    }

    .status-container {
      @apply text-center mr-20;

      flex: 0 0 105px;
    }

    .extra-guests-container {
      flex: 0 0 160px;
    }

    .email-container {
      flex: 1 1 240px;

      .email {
        @apply block !important;
      }
    }
  }

  .extra-guests-button {
    @apply px-8 py-4 rounded border border-dashed border-gray-400 leading-tight;

    &:hover,
    &:focus {
      @apply border-status-checked-in text-status-checked-in outline-none;
    }
  }

  .check-in-guest-button {
    @apply p-10 text-gray-500 border-gray-300;

    flex: 0 0 auto;

    .check-in-guest-icon {
      width: 14px;
      height: 14px;
      vertical-align: middle;
    }

    /* The fill must only reflect the stored status: a tapped button keeps focus, and a focus fill made a
       reset guest still look checked in */
    &.is-checked-in {
      @apply text-white bg-status-checked-in border-status-checked-in;
    }
  }

  .guest-actions {
    @apply ml-25;

    flex: 0 0 auto;

    .edit-guest-button {
      @apply p-10;

      .edit-guest-icon {
        width: 14px;
        height: 14px;
        vertical-align: middle;
      }
    }

  }
}

@screen touch {
  .guest-row .guest-data {
    .name-vip-container {
      flex: 0 0 140px;
    }

    .extra-guests-container {
      @apply mr-0;

      flex: 0 0 160px;
    }

    .email-container {
      @apply mr-0;

      flex: 0 2 100px;
    }
  }
}

@screen mobile {
  .guest-row .guest-data {
    .name-vip-container {
      flex: 0 0 150px;
    }

    .extra-guests-container {
      flex: 0 0 100px;
    }

    .email-container {
      @apply hidden;
    }
  }
}

@screen small {
  .guest-row .guest-data {
    .name-vip-container {
      flex: 0 0 100px;
    }
  }
}

@screen any-pointer-fine {
  .guest-row:hover {
    @apply shadow-3dp;
  }

  .guest-row .check-in-guest-button:hover {
    @apply text-white bg-status-checked-in border-status-checked-in;
  }
}

/* Transitions */
.name-vip-animation-item {
  transition: all 0.3s ease;
}

.name-vip-animation-enter, .name-vip-animation-leave-to {
  @apply opacity-0;

  transform: translateY(20px);
}

.name-vip-animation-leave-active {
  @apply opacity-0;
  @apply absolute;

  transform: translateY(15px);
}

</style>
